import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchChantiers } from '@/store/thunks/chantierThunks';
import { fetchNatures } from '@/store/thunks/natureThunks';
import { createSortie, updateSortie } from '@/store/thunks/movementThunks';
import api from '@/api/client';
import { parseListResponse } from '@/api/types';
import type { MovementRecord } from '@/store/slices/movementSlice';
import type { Chantier } from '@/types';
import type { Citerne } from '@/types';
import type { Engin } from '@/types';
import type { Nature } from '@/types';
import { Input, Button, SearchSelect, Textarea } from '@/components/ui';

const baseSchema = {
  chantierId: Yup.string().required('Chantier obligatoire'),
  citerneId: Yup.string().required('Citerne obligatoire'),
  enginId: Yup.string().required('Engin obligatoire'),
  date: Yup.string().required('Date obligatoire'),
  compteur: Yup.number().required('Compteur obligatoire'),
  quantite: Yup.number().required('Quantité obligatoire').min(0),
  avance: Yup.boolean(),
  remarque: Yup.string(),
};

interface SortieFormProps {
  sortie?: MovementRecord | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function SortieForm({ sortie, type, onClose, onSuccess }: SortieFormProps) {
  const dispatch = useAppDispatch();
  const chantiers = useAppSelector((s: RootState) => s.chantier.chantiers);
  const natures = useAppSelector((s: RootState) => s.nature.natures);
  const [citernesByChantier, setCiternesByChantier] = useState<Citerne[]>([]);
  const [enginsByChantier, setEnginsByChantier] = useState<Engin[]>([]);
  const [chantierId, setChantierId] = useState<string>(sortie?.chantier ? String((sortie.chantier as { id?: number }).id) : '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchChantiers());
    dispatch(fetchNatures());
  }, [dispatch]);

  useEffect(() => {
    if (!chantierId) {
      setCiternesByChantier([]);
      setEnginsByChantier([]);
      return;
    }
    (async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          api.get(`/citernes/chantier/${chantierId}`),
          api.get(`/engins/chantier/${chantierId}`),
        ]);
        setCiternesByChantier(parseListResponse<Citerne>(cRes.data));
        setEnginsByChantier(parseListResponse<Engin>(eRes.data));
      } catch {
        setCiternesByChantier([]);
        setEnginsByChantier([]);
      }
    })();
  }, [chantierId]);

  const formik = useFormik({
    initialValues: {
      chantierId: sortie?.chantier ? String((sortie.chantier as { id?: number }).id) : '',
      citerneId: sortie?.citerne ? String((sortie.citerne as { id?: number }).id) : '',
      enginId: sortie?.engine ? String((sortie.engine as { id?: number }).id) : '',
      date: sortie?.date ? (sortie.date as string).toString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      compteur: sortie?.compteur ?? '',
      quantite: sortie?.quantite ?? '',
      avance: Boolean(sortie?.avance),
      quantite_excutee: sortie?.quantite_excutee ?? 1,
      natureId: sortie?.nature ? String((sortie.nature as { id?: number }).id) : '',
      prix: sortie?.prix ?? '',
      remarque: (sortie?.remarque as string) ?? '',
    },
    validationSchema: Yup.lazy((values: { avance?: boolean }) => {
      const schemaObj: Record<string, Yup.AnySchema> = { ...baseSchema };
      if (!values.avance) {
        schemaObj.natureId = Yup.string().required('Nature obligatoire');
        schemaObj.quantite_excutee = Yup.number().required().min(0);
      }
      return Yup.object(schemaObj);
    }),
    onSubmit: async (values) => {
      const chantier = chantiers.find((c: Chantier) => String(c.id) === values.chantierId);
      const citerne = citernesByChantier.find((c) => String(c.id) === values.citerneId);
      const engin = enginsByChantier.find((e) => String(e.id) === values.enginId);
      if (!chantier || !citerne || !engin) return;
      const payload: MovementRecord = {
        chantier: { id: chantier.id, code: chantier.code },
        citerne: { id: citerne.id, code: citerne.code },
        engine: { id: engin.id, code: engin.code },
        date: values.date,
        compteur: Number(values.compteur),
        quantite: Number(values.quantite),
        avance: values.avance,
        remarque: values.remarque || undefined,
        prix: values.avance ? Number(values.prix) || 0 : undefined,
      };
      if (!values.avance && values.natureId) {
        const nature = natures.find((n: Nature) => String(n.id) === values.natureId);
        if (nature) {
          (payload as Record<string, unknown>).nature = { id: nature.id };
          (payload as Record<string, unknown>).quantite_excutee = Number(values.quantite_excutee);
        }
      }
      setSubmitError(null);
      if (type === 'update' && sortie?.id) {
        const res = await dispatch(updateSortie({ ...payload, id: sortie.id }));
        if (updateSortie.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        } else if (updateSortie.rejected.match(res) && res.payload) {
          setSubmitError(typeof res.payload === 'string' ? res.payload : 'Erreur lors de la modification');
        }
      } else {
        const res = await dispatch(createSortie(payload));
        if (createSortie.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        } else if (createSortie.rejected.match(res) && res.payload) {
          setSubmitError(typeof res.payload === 'string' ? res.payload : 'Erreur lors de la création');
        }
      }
    },
  });

  // Sync chantierId with formik for loading citernes/engins
  useEffect(() => {
    setChantierId(formik.values.chantierId);
  }, [formik.values.chantierId]);

  const chantierOptions = chantiers.map((c: Chantier) => ({ value: String(c.id), label: c.code ?? c.nom ?? String(c.id) }));
  const citerneOptions = citernesByChantier.map((c) => ({ value: String(c.id), label: c.code ?? String(c.id) }));
  const enginOptions = enginsByChantier.map((e) => ({ value: String(e.id), label: e.code ?? String(e.id) }));
  const natureOptions = natures.map((n: Nature) => ({ value: String(n.id), label: n.nom ?? String(n.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <div>
        <SearchSelect label="Chantier" options={chantierOptions} value={formik.values.chantierId} onChange={(v) => { formik.setFieldValue('chantierId', v); formik.setFieldValue('citerneId', ''); formik.setFieldValue('enginId', ''); }} onBlur={() => formik.setFieldTouched('chantierId', true)} error={formik.touched.chantierId && formik.errors.chantierId ? String(formik.errors.chantierId) : undefined} searchPlaceholder="Rechercher un chantier…" />
      </div>
      <div className="flex gap-4">
        <SearchSelect label="Citerne" options={citerneOptions} value={formik.values.citerneId} onChange={(v) => formik.setFieldValue('citerneId', v)} onBlur={() => formik.setFieldTouched('citerneId', true)} disabled={!formik.values.chantierId} error={formik.touched.citerneId && formik.errors.citerneId ? String(formik.errors.citerneId) : undefined} searchPlaceholder="Rechercher une citerne…" />
        <SearchSelect label="Engin" options={enginOptions} value={formik.values.enginId} onChange={(v) => formik.setFieldValue('enginId', v)} onBlur={() => formik.setFieldTouched('enginId', true)} disabled={!formik.values.chantierId} error={formik.touched.enginId && formik.errors.enginId ? String(formik.errors.enginId) : undefined} searchPlaceholder="Rechercher un engin…" />
      </div>
      <div className="flex gap-4">
        <Input label="Date" type="date" id="date" {...formik.getFieldProps('date')} error={formik.touched.date && formik.errors.date ? String(formik.errors.date) : undefined} />
        <Input label="Compteur" type="number" id="compteur" {...formik.getFieldProps('compteur')} error={formik.touched.compteur && formik.errors.compteur ? String(formik.errors.compteur) : undefined} />
        <Input label="Quantité" type="number" id="quantite" {...formik.getFieldProps('quantite')} error={formik.touched.quantite && formik.errors.quantite ? String(formik.errors.quantite) : undefined} />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={formik.values.avance} onChange={(e) => formik.setFieldValue('avance', e.target.checked)} className="rounded border-slate-300" />
          <span className="text-sm font-medium text-slate-700">Avance</span>
        </label>
        {formik.values.avance && (
          <Input label="Prix" type="number" id="prix" {...formik.getFieldProps('prix')} />
        )}
      </div>
      {!formik.values.avance && (
        <div className="flex gap-4">
          <SearchSelect label="Nature travaux" options={natureOptions} value={formik.values.natureId} onChange={(v) => formik.setFieldValue('natureId', v)} onBlur={() => formik.setFieldTouched('natureId', true)} error={formik.touched.natureId && formik.errors.natureId ? String(formik.errors.natureId) : undefined} searchPlaceholder="Rechercher une nature…" />
          <Input label="Quantité exécutée" type="number" id="quantite_excutee" {...formik.getFieldProps('quantite_excutee')} error={formik.touched.quantite_excutee && formik.errors.quantite_excutee ? String(formik.errors.quantite_excutee) : undefined} />
        </div>
      )}
      <Textarea label="Remarque" id="remarque" rows={3} {...formik.getFieldProps('remarque')} />
      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {submitError}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
